import { createFileRoute, Link } from '@tanstack/react-router'
import { usePredefinedSEOMeta } from '@/hooks/use-seo-meta'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import {
  ContentGrid,
  FeaturedContentCard,
} from '@/components/ui/content-card'
import { ServiceCard, ServiceGrid, FeatureCard, TestimonialCard } from '@/components/ui/service-card'
import { LeadCaptureForm } from '@/components/ui/lead-capture-form'
import { Section, SectionHeader, StatsSection, CTASection } from '@/components/ui/section'
import {
  BookOpen,
  TrendingUp,
  Users,
  Award,
  Zap,
  Brain,
  BarChart3,
  CheckCircle2,
  GraduationCap,
  Briefcase,
  FileText,
} from 'lucide-react'

export const Route = createFileRoute('/')({
  component: HomeComponent,
})

function HomeComponent() {
  // Apply SEO meta tags for home page
  usePredefinedSEOMeta('home')

  // Query for content (reusing algorithms for now as educational content)


  // Note: We're not using API stats anymore since they're not set up yet
  // Future: fetch actual stats from backend when available

  // Sample educational content data (this would come from a content CMS)
  const educationalContent = [
    {
      id: 1,
      title: 'Introduction to Algorithmic Trading',
      description: 'Learn the fundamentals of algorithmic trading, from basic concepts to advanced strategies used by professionals.',
      category: 'Getting Started',
      readTime: '15 min read',
      author: 'AIAlgo Team',
      type: 'guide' as const,
      tags: ['beginner', 'fundamentals', 'trading-basics'],
    },
    {
      id: 2,
      title: 'Building Your First Trading Bot',
      description: 'A step-by-step guide to creating your first automated trading system using Python and popular APIs.',
      category: 'Tutorial',
      readTime: '25 min read',
      author: 'John Smith',
      type: 'article' as const,
      tags: ['tutorial', 'python', 'implementation'],
    },
    {
      id: 3,
      title: 'Risk Management for Algorithmic Traders',
      description: 'Essential risk management techniques to protect your capital and maximize long-term profitability.',
      category: 'Risk Management',
      readTime: '20 min read',
      author: 'Sarah Johnson',
      type: 'article' as const,
      tags: ['risk-management', 'portfolio', 'strategy'],
    },
  ]

  const statsData = [
    { value: 'New', label: 'Platform Launch' },
    { value: 'Open', label: 'Algorithm Marketplace' },
    { value: 'Beta', label: 'Current Status' },
    { value: 'Free', label: 'Early Access' },
  ]

  const services = [
    {
      title: 'Futures Consulting',
      description: 'Expert guidance on implementing futures algorithmic trading strategies tailored to your needs.',
      icon: <Briefcase className="h-6 w-6" />,
      features: [
        '1-on-1 strategy development',
        'Custom futures algorithm design',
        'Order flow analysis consulting',
        'Performance optimization',
        'Ongoing support',
      ],
      ctaText: 'Apply for Consultation',
    },
    {
      title: 'Futures Trading Tools',
      description: 'Premium tools and software to enhance your futures algorithmic trading capabilities.',
      icon: <Zap className="h-6 w-6" />,
      features: [
        'Backtesting for futures',
        'Order flow visualization',
        'Real-time liquidity analytics',
        'Risk monitoring',
        'API access',
      ],
      ctaText: 'Explore Tools',
      highlighted: true,
    },
    {
      title: 'Trading Education',
      description: 'Comprehensive courses cover futures market dynamics and algorithmic execution.',
      icon: <GraduationCap className="h-6 w-6" />,
      features: [
        'Futures market fundamentals',
        'Order flow mastery',
        'Algorithmic execution patterns',
        'Certificate of completion',
        'Community access',
      ],
      ctaText: 'Start Learning',
    },
  ]

  const features = [
    {
      title: 'Order Flow Labs',
      description: 'Visualize market depth and liquidity with our advanced heatmap and volume profile tools.',
      icon: <TrendingUp className="h-6 w-6 text-blue-400" />,
      link: '/order-flow-labs',
    },
    {
      title: 'Expert-Led Content',
      description: 'Learn from industry professionals with years of experience in algorithmic trading.',
      icon: <Users className="h-6 w-6" />,
    },

    {
      title: 'Proven Strategies',
      description: 'Access battle-tested trading strategies with documented performance history.',
      icon: <TrendingUp className="h-6 w-6" />,
    },
    {
      title: 'Community Support',
      description: 'Join a thriving community of traders sharing insights and helping each other succeed.',
      icon: <Award className="h-6 w-6" />,
    },
    {
      title: 'AI-Powered Insights',
      description: 'Leverage artificial intelligence to optimize your trading strategies and decision-making.',
      icon: <Brain className="h-6 w-6" />,
    },
    {
      title: 'Advanced Analytics',
      description: 'Comprehensive analytics and reporting to track and improve your trading performance.',
      icon: <BarChart3 className="h-6 w-6" />,
    },
  ]

  // Placeholder - testimonials would come from actual user reviews
  const testimonials: Array<{ quote: string; author: string; role: string }> = []

  const handleNewsletterSubmit = async (data: { email: string; name?: string }) => {
    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, source: 'homepage_newsletter' })
      })

      if (!response.ok) throw new Error('Subscription failed')

      toast.success('Successfully subscribed to newsletter!')
    } catch (error) {
      toast.error('Failed to subscribe. Please try again later.')
      console.error('Newsletter error:', error)
    }
  }

  const handleConsultationSubmit = async (data: { email: string; name?: string }) => {
    try {
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, referral_source: 'homepage_consultation' })
      })

      if (!response.ok) throw new Error('Submission failed')

      toast.success('Consultation request received! We will contact you shortly.')
    } catch (error) {
      toast.error('Failed to submit request. Please try again later.')
      console.error('Consultation error:', error)
    }
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="gradient-hero py-20 md:py-32">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 badge badge-primary mb-6">
              <TrendingUp className="h-4 w-4" />
              <span>Master <span className="italic">Futures</span> Algorithmic Trading</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
              Master the Art of <span className="text-gradient">Futures Trading</span> with AI
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              The premier platform for discovering, building, and deploying high-performance futures trading algorithms using order flow and market sentiment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/about">
                <Button size="lg" className="text-lg px-8 py-3">
                  Start Learning
                  <BookOpen className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/marketplace">
                <Button variant="outline" size="lg" className="text-lg px-8 py-3">
                  Explore Services
                  <Briefcase className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <StatsSection stats={statsData} />

      {/* Features Section */}
      <Section>
        <SectionHeader
          title="Why Choose AIAlgo.trading?"
          description="Comprehensive resources and expert guidance to accelerate your algorithmic trading journey"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            // @ts-ignore - link property is optional and added dynamically
            if (feature.link) {
              return (
                <Link key={index} to={feature.link} className="block h-full">
                  <FeatureCard {...feature} className="h-full border-blue-500/20 bg-blue-500/5 hover:border-blue-500/50" />
                </Link>
              )
            }
            return <FeatureCard key={index} {...feature} />
          })}
        </div>
      </Section>

      {/* Educational Content Section */}
      <Section variant="muted">
        <SectionHeader
          title="Featured Educational Content"
          description="Start your journey with our most popular learning resources"
        />
        <ContentGrid columns={3}>
          {educationalContent.map((content) => (
            <FeaturedContentCard key={content.id} {...content} />
          ))}
        </ContentGrid>
        <div className="text-center mt-8">
          <Link to="/about">
            <Button variant="outline" size="lg">
              Browse All Content
              <FileText className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </Section>

      {/* Services Section */}
      <Section>
        <SectionHeader
          title="Professional Services"
          description="Expert solutions to help you succeed in algorithmic trading"
        />
        <ServiceGrid columns={3}>
          {services.map((service, index) => (
            <ServiceCard key={index} {...service} />
          ))}
        </ServiceGrid>
      </Section>

      {/* Testimonials Section - only show when we have real reviews */}
      {testimonials.length > 0 && (
        <Section variant="muted">
          <SectionHeader
            title="What Our Users Say"
            description="Reviews from our community"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index} {...testimonial} />
            ))}
          </div>
        </Section>
      )}

      {/* Lead Capture & Newsletter Section */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Newsletter Signup */}
          <div>
            <SectionHeader
              align="left"
              title="Stay Updated"
              description="Get the latest insights, strategies, and resources delivered to your inbox"
              titleClassName="text-2xl"
              descriptionClassName="mx-0"
            />
            <LeadCaptureForm
              type="newsletter"
              showName={true}
              onSubmit={handleNewsletterSubmit}
            />
          </div>

          {/* Consultation CTA */}
          <div>
            <SectionHeader
              align="left"
              title="Ready to Get Started?"
              description="Book a free consultation to discuss your algorithmic trading goals"
              titleClassName="text-2xl"
              descriptionClassName="mx-0"
            />
            <LeadCaptureForm
              type="consultation"
              showName={true}
              submitText="Book Free Consultation"
              onSubmit={handleConsultationSubmit}
            />
          </div>
        </div>
      </Section>

      {/* Learning Paths Section */}
      <Section variant="muted">
        <SectionHeader
          title="Structured Learning Paths"
          description="Follow our curated learning journeys designed for all skill levels"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card p-6 hover-lift">
            <div className="badge badge-success mb-4">Beginner</div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Trading Fundamentals
            </h3>
            <p className="text-muted-foreground mb-4">
              Build a strong foundation in algorithmic trading concepts and basic strategies.
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>12+ hours of content</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>Hands-on projects</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>Certificate included</span>
              </li>
            </ul>
            <Link to="/about">
              <Button variant="outline" className="w-full">
                Start Path
              </Button>
            </Link>
          </div>

          <div className="card p-6 hover-lift border-primary/30 ring-1 ring-primary/20">
            <div className="badge badge-primary mb-4">Intermediate</div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Strategy Development
            </h3>
            <p className="text-muted-foreground mb-4">
              Learn to develop and optimize your own trading algorithms with advanced techniques.
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>20+ hours of content</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>Live trading examples</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>Expert feedback</span>
              </li>
            </ul>
            <Link to="/backtesting">
              <Button className="w-full">
                Continue Path
              </Button>
            </Link>
          </div>

          <div className="card p-6 hover-lift">
            <div className="badge badge-warning mb-4">Advanced</div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Professional Trading
            </h3>
            <p className="text-muted-foreground mb-4">
              Master institutional-grade strategies and portfolio management techniques.
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>30+ hours of content</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>1-on-1 mentoring</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>Job placement support</span>
              </li>
            </ul>
            <Link to="/developers">
              <Button variant="outline" className="w-full">
                Explore Path
              </Button>
            </Link>
          </div>
        </div>
      </Section>

      {/* Final CTA Section */}
      <CTASection
        title="Ready to Transform Your Trading?"
        description="Join our community of successful algorithmic traders. Get access to expert knowledge, proven strategies, and cutting-edge tools."
        primaryCTA={{
          text: 'Start Learning Now',
          onClick: () => console.log('Start learning'),
        }}
        secondaryCTA={{
          text: 'Schedule Consultation',
          onClick: () => console.log('Schedule consultation'),
        }}
      />
    </div>
  )
}
